import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className="border-b border-border bg-background">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="text-xl font-semibold text-foreground hover:text-primary transition-colors"
            >
              Bardus AI
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Terms of Service
          </h1>
          <p className="text-muted-foreground">Last updated: January 1, 2024</p>
        </div>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing and using Bardus AI ("Service"), you accept and agree
              to be bound by the terms and provision of this agreement. If you
              do not agree to abide by the above, please do not use this
              Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              2. Description of Service
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Bardus AI provides AI-powered sound effect generation services.
              The Service includes:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Text-to-sound generation using AI technology</li>
              <li>Sound library management and organization</li>
              <li>Download and export capabilities</li>
              <li>Collaboration features for team accounts</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              3. User Accounts
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              To use certain features of the Service, you must register for an
              account. You agree to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your password</li>
              <li>Promptly update your information if it changes</li>
              <li>
                Accept responsibility for all activities under your account
              </li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              4. Acceptable Use
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Generate content that is illegal, harmful, or offensive</li>
              <li>Infringe on intellectual property rights</li>
              <li>Distribute malware or harmful code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use the Service for any unlawful purpose</li>
              <li>Resell or redistribute the Service without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              5. Intellectual Property Rights
            </h2>
            <h3 className="text-xl font-semibold mb-2 text-foreground">
              5.1 Your Content
            </h3>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              You retain all rights to the sound effects you generate using our
              Service. You grant us a limited license to store and process your
              content solely for providing the Service.
            </p>

            <h3 className="text-xl font-semibold mb-2 text-foreground">
              5.2 Our Service
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              The Service, including its original content, features, and
              functionality, is owned by Bardus AI and is protected by
              international copyright, trademark, and other intellectual
              property laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              6. Payment Terms
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              For paid subscriptions:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Payments are processed securely through Stripe</li>
              <li>Subscriptions automatically renew unless cancelled</li>
              <li>You can cancel anytime from your account settings</li>
              <li>No refunds for partial months</li>
              <li>Price changes will be notified 30 days in advance</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              7. Commercial Use License
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Paid plans include a commercial use license that allows you to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Use generated sounds in commercial projects</li>
              <li>Include sounds in products for sale</li>
              <li>Use sounds in monetized content</li>
              <li>
                Transfer usage rights to clients (with active subscription)
              </li>
            </ul>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Free plan users may only use generated sounds for personal,
              non-commercial purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              8. Limitation of Liability
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              To the maximum extent permitted by law, Bardus AI shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, or any loss of profits or revenues, whether
              incurred directly or indirectly, or any loss of data, use,
              goodwill, or other intangible losses.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              9. Disclaimer of Warranties
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service is provided "as is" and "as available" without any
              warranties of any kind, either express or implied. We do not
              guarantee that the Service will be uninterrupted, secure, or
              error-free.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              10. Indemnification
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify and hold harmless Bardus AI and its
              officers, directors, employees, and agents from any claims,
              damages, losses, liabilities, costs, and expenses arising from
              your use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              11. Termination
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We may terminate or suspend your account immediately, without
              prior notice or liability, for any reason, including breach of
              these Terms. Upon termination, your right to use the Service will
              cease immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              12. Governing Law
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms shall be governed by and construed in accordance with
              the laws of the United States, without regard to its conflict of
              law provisions. Any disputes shall be resolved in the courts of
              competent jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              13. Changes to Terms
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify or replace these Terms at any time.
              If a revision is material, we will provide at least 30 days notice
              prior to any new terms taking effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              14. Contact Information
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-muted-foreground mt-2">
              Email:{" "}
              <a
                href="mailto:legal@bardus.ai"
                className="text-primary hover:text-primary/80"
              >
                legal@bardus.ai
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6 mt-16">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-muted-foreground text-sm">
            &copy; 2024 Bardus AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
